import {
  IAction,
  type IStep,
  useChatMessages,
  useConfig
} from '@chainlit/react-client';

import CopyButton from '@/components/CopyButton';
import EmailButton from '@/components/EmailButton';
import SpeakerButton from '@/components/SpeakerButton';

import MessageActions from './Actions';
import { DebugButton } from './DebugButton';
import { FeedbackButtons } from './FeedbackButtons';

interface Props {
  message: IStep;
  actions: IAction[];
  run?: IStep;
  isStreaming?: boolean;
}

const MessageButtons = ({ message, actions, run, isStreaming }: Props) => {
  const { config } = useConfig();
  const { firstInteraction } = useChatMessages();

  const isUser = message.type === 'user_message';
  const isAsk = message.waitForAnswer;
  const hasContent = !!message.output;
  const showCopyButton = !!run && hasContent && !isUser && !isAsk;

  const messageActions = actions.filter((a) => a.forId === message.id);

  const showDebugButton =
    !!config?.debugUrl && !!message.threadId && !!firstInteraction && !!run;

  const show = showCopyButton || showDebugButton || messageActions?.length;

  const streaming =
    typeof isStreaming === 'boolean' ? isStreaming : message.streaming;

  if (!show || streaming) {
    return null;
  }

  return (
    <div className="-ml-1.5 w-full">
      <div className="flex items-center flex-wrap justify-between">
        <div className="flex items-center flex-wrap">
          {run ? <FeedbackButtons message={run} /> : null}
          {showCopyButton ? <CopyButton content={message.output} messageId={message.id} /> : null}
          {showCopyButton ? <EmailButton content={message.output} messageId={message.id} /> : null}
          {showCopyButton ? <SpeakerButton content={message.output} messageId={message.id} /> : null}
          {messageActions.length ? (
            <MessageActions actions={messageActions} />
          ) : null}
          {showDebugButton ? (
            <DebugButton debugUrl={config.debugUrl!} step={message} />
          ) : null}
        </div>
        <span className="text-xs text-muted-foreground italic ml-auto hidden sm:block">
          AI-generated content may be incorrect.
        </span>
      </div>
      <div className="flex justify-center sm:hidden mt-1">
        <span className="text-xs text-muted-foreground italic">
          AI-generated content may be incorrect.
        </span>
      </div>
    </div>
  );
};

export { MessageButtons };
